import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  resolveLanguage,
  toI18nextCode
} from '@/i18n/languages'

/**
 * Lightweight language toggle for the auth screens. Unlike the global
 * LanguageSwitcher (white text, meant for dark headers), this is a compact
 * pill that reads well on the light form panel.
 */
export function AuthLanguageSwitcher({ className }: { className?: string }) {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()

  const current = resolveLanguage(i18n.language)

  const changeLanguage = async (value: SupportedLanguage) => {
    if (value === current) return
    await i18n.changeLanguage(toI18nextCode(value))
    localStorage.setItem('preferred_language', value)
    await queryClient.invalidateQueries()
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white/70 p-1 backdrop-blur',
        className
      )}
    >
      {SUPPORTED_LANGUAGES.map((lang) => {
        const active = current === lang
        return (
          <button
            key={lang}
            type="button"
            onClick={() => changeLanguage(lang)}
            className={cn(
              'rounded-full px-3 py-1 text-sm font-semibold transition-colors',
              active
                ? 'bg-primary text-white'
                : 'text-ink/60 hover:text-ink'
            )}
          >
            {LANGUAGE_LABELS[lang]}
          </button>
        )
      })}
    </div>
  )
}
